import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

/**
 * The safe common display shape for a selected Wage Determination. It is
 * deliberately presentation-only: callers decide whether an artifact can be
 * downloaded, printed, or expanded.
 */
export interface WageDeterminationResultCardData {
  wd_number: string;
  construction_type: string;
  geographic_scope: string;
  modification_date: string;
  modification_number?: string;
  publication_date?: string;
  effective_date?: string;
  reference_date?: string;
  status?: string;
  source_name?: string;
  /** Server-derived canonical SAM.gov decision/revision URL for a safe reference. */
  determination_url?: string;
  sam_url?: string;
  full_text?: string;
}

interface WageDeterminationResultCardProps {
  determination: WageDeterminationResultCardData;
  cardId: string;
  expanded?: boolean;
  onToggle?: (cardId: string) => void;
  onDownloadText?: (wdNumber: string) => void;
  onPrint?: (fullText: string, wdNumber: string) => void;
  downloading?: boolean;
}

/**
 * Shared selected-result card for the Wage Determination workflow and Stage
 * 623. Stage 623 supplies reference-only data and omits artifact actions.
 */
const WageDeterminationResultCard: React.FC<WageDeterminationResultCardProps> = ({
  determination,
  cardId,
  expanded = false,
  onToggle,
  onDownloadText,
  onPrint,
  downloading = false,
}) => {
  const theme = useTheme();
  const isInteractive = Boolean(onToggle);
  const toggle = () => onToggle?.(cardId);

  return (
    <Card
      data-testid="wage-determination-result-card"
      sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}
    >
      <CardContent
        sx={{
          pb: 1,
          ...(isInteractive ? { cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } } : {}),
        }}
        onClick={isInteractive ? toggle : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">
                {determination.sam_url ? (
                  <a
                    href={determination.sam_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {determination.geographic_scope}
                  </a>
                ) : determination.geographic_scope}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip label={determination.construction_type} size="small" color="primary" variant="outlined" />
              <Chip label={`Modified: ${determination.modification_date}`} size="small" variant="outlined" />
              {determination.status && <Chip label={determination.status} size="small" variant="outlined" />}
              {determination.modification_number && <Chip label={`Revision: ${determination.modification_number}`} size="small" variant="outlined" />}
              {determination.publication_date && <Chip label={`Published: ${determination.publication_date}`} size="small" variant="outlined" />}
              {determination.effective_date && <Chip label={`Effective: ${determination.effective_date}`} size="small" variant="outlined" />}
              {determination.reference_date && <Chip label={`As of: ${determination.reference_date}`} size="small" variant="outlined" />}
            </Box>
            <Typography variant="body2" color="text.secondary"><strong>WD Number:</strong> {determination.wd_number}</Typography>
            {determination.source_name && <Typography variant="body2" color="text.secondary"><strong>Source:</strong> {determination.source_name}</Typography>}
            {determination.determination_url && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <a href={determination.determination_url} target="_blank" rel="noopener noreferrer">
                  View Wage Determination
                </a>
              </Typography>
            )}
          </Box>
          {onDownloadText && (
            <Tooltip title="Download as TXT">
              <IconButton
                size="small"
                onClick={(event) => { event.stopPropagation(); onDownloadText(determination.wd_number); }}
                disabled={downloading}
              >
                {downloading ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {onPrint && (
            <Tooltip title="Print">
              <IconButton
                size="small"
                onClick={(event) => { event.stopPropagation(); onPrint(determination.full_text || '', determination.wd_number); }}
                disabled={!determination.full_text}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isInteractive && (
            <IconButton onClick={(event) => { event.stopPropagation(); toggle(); }} size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </CardContent>
      {isInteractive && (
        <Collapse in={expanded}>
          <Divider />
          <CardContent>
            {determination.full_text && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Full Text</Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, maxHeight: 600, overflow: 'auto', backgroundColor: theme.palette.surfaceMuted, fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {determination.full_text}
                </Paper>
              </Box>
            )}
          </CardContent>
        </Collapse>
      )}
    </Card>
  );
};

export default WageDeterminationResultCard;
